import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useToast } from '../context/ToastContext';
import {
  ArrowLeft, Building2, UserPlus, X, Copy, Check, Trash2, CheckCircle,
  KeyRound, RefreshCw,
  LayoutDashboard, Users, Mic, CreditCard, Phone, Settings, Plug,
  Save, Loader, Play, Pause, PhoneCall, Settings as SettingsIcon,
  Search, Download, Upload, Globe, Mail, Ban, FileDown,
} from 'lucide-react';

const VOICES = [
  { id: 'kari', name: 'Kari', gender: 'Kvinne', description: 'Varm og profesjonell kvinnestemme' },
  { id: 'lars', name: 'Lars', gender: 'Mann', description: 'Rolig og tydelig mannsstemme' },
];

const PLANS = {
  Start: 1990,
  Pro: 5990,
  Bedrift: 9990,
};

const TABS = [
  { id: 'overview',      label: 'Oversikt',       icon: LayoutDashboard },
  { id: 'onboarding',    label: 'Onboarding',     icon: CheckCircle },
  { id: 'users',         label: 'Brukere',        icon: Users },
  { id: 'agent',         label: 'AI-agent',       icon: Mic },
  { id: 'billing',       label: 'Fakturering',    icon: CreditCard },
  { id: 'calls',         label: 'Samtaler',       icon: Phone },
  { id: 'contacts',      label: 'Kontakter',      icon: Users },
  { id: 'settings',      label: 'Innstillinger',  icon: Settings },
  { id: 'integrations',  label: 'Integrasjoner',  icon: Plug },
];

function formatRelativeDaysNO(iso) {
  if (!iso) return 'Aldri';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days < 1) {
    const hours = Math.floor(diffMs / 3600000);
    if (hours < 1) return 'Nå nettopp';
    return `${hours} ${hours === 1 ? 'time' : 'timer'} siden`;
  }
  if (days === 1) return 'I går';
  if (days < 30) return `${days} dager siden`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? 'måned' : 'måneder'} siden`;
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? 'år' : 'år'} siden`;
}

export default function AdminCompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  // Core state
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const validCompanyTabs = ['overview', 'onboarding', 'users', 'agent', 'billing', 'calls'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(validCompanyTabs.includes(tabFromUrl) ? tabFromUrl : 'overview');
  const handleCompanyTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
  };

  // Edit + invite
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Credentials
  const [credentials, setCredentials] = useState(null);
  const [resettingPw, setResettingPw] = useState(false);
  const [newPwReveal, setNewPwReveal] = useState(null);
  const [removeUserConfirm, setRemoveUserConfirm] = useState(null); // userId
  const [resetPwConfirm, setResetPwConfirm] = useState(false);
  const [suspendConfirm, setSuspendConfirm] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [companyId]);

  useEffect(() => {
    if (!newPwReveal) return;
    const timer = setTimeout(() => setNewPwReveal(null), 60000);
    return () => clearTimeout(timer);
  }, [newPwReveal]);

  const loadData = async () => {
    setLoadError(null);
    try {
      const [compRes, usersRes, callsRes, statsRes] = await Promise.all([
        api.get(`/api/admin/companies/${companyId}`),
        api.get(`/api/admin/companies/${companyId}/users`),
        api.get(`/api/calls?company_id=${companyId}&limit=50&sort=created_at&order=desc`),
        api.get(`/api/admin/companies/${companyId}/stats`).catch(() => ({ data: null })),
      ]);
      setCompany(compRes.data);
      setUsers(usersRes.data.users);
      setRecentCalls(callsRes.data.calls);
      setStats(statsRes.data);
      try {
        const credRes = await api.get(`/api/admin/clients/${companyId}/credentials`);
        setCredentials(credRes.data);
      } catch (e) { setCredentials(null); }
      setEditForm({
        name: compRes.data.name || '',
        agent_id: compRes.data.agent_id || '',
        phone_number: compRes.data.phone_number || '',
        active: compRes.data.active,
        onboarding_status: compRes.data.onboarding_status || 'pending',
        onboarding_notes: compRes.data.onboarding_notes || '',
        contact_email: compRes.data.contact_email || '',
        contact_phone: compRes.data.contact_phone || '',
        monthly_revenue: compRes.data.monthly_revenue || 0,
        admin_notes: compRes.data.admin_notes || '',
        language: compRes.data.language || 'no',
        transfer_number: compRes.data.transfer_number || '',
        brand: compRes.data.brand || 'no',
        business_hours: compRes.data.business_hours || '',
        webhook_url: compRes.data.webhook_url || '',
      });
    } catch (err) {
      console.error(err);
      setLoadError('Kunne ikke laste bedriftsdata. Prøv igjen.');
    }
    finally { setLoading(false); }
  };

  const handleInvite = async () => {
    try {
      const { data } = await api.post('/api/admin/invite', { email: inviteEmail, company_id: companyId, role: 'client' });
      setInviteLink(data.invite_url);
      setInviteEmail('');
    } catch (err) { console.error(err); toast.error('Kunne ikke sende invitasjon'); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success('Invitasjonslenke kopiert!'); })
      .catch(() => toast.error('Kunne ikke kopiere lenke'));
  };

  const handleUpdate = async () => {
    try { await api.put(`/api/admin/companies/${companyId}`, editForm); setEditing(false); loadData(); toast.success('Bedrift oppdatert'); }
    catch (err) { console.error(err); toast.error('Kunne ikke oppdatere bedrift'); }
  };

  const handleRemoveUser = async (userId) => {
    if (removeUserConfirm !== userId) { setRemoveUserConfirm(userId); return; }
    try { await api.delete(`/api/admin/companies/${companyId}/users/${userId}`); toast.success('Bruker fjernet'); loadData(); }
    catch (err) { console.error(err); toast.error('Kunne ikke fjerne bruker'); }
    finally { setRemoveUserConfirm(null); }
  };

  const handleResetPassword = async () => {
    if (!resetPwConfirm) { setResetPwConfirm(true); return; }
    setResettingPw(true);
    try {
      const { data } = await api.post(`/api/admin/clients/${companyId}/credentials/reset`, {});
      setNewPwReveal(data.temp_password || data.password);
      setCredentials({ ...data, temp_password: undefined, password: undefined, temp_password_available: true });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Kunne ikke tilbakestille passord');
    }
    finally { setResettingPw(false); setResetPwConfirm(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success('Kopiert!'))
      .catch(() => toast.error('Kunne ikke kopiere'));
  };

  if (loading) return <div className="animate-pulse"><div className="h-8 bg-navy-800 rounded w-48 mb-6" /><div className="h-64 bg-navy-800 rounded-lg" /></div>;
  if (loadError) return (
    <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
      <div className="text-red-300 mb-3">{loadError}</div>
      <button onClick={() => { setLoading(true); loadData(); }} className="px-4 py-2 bg-red-700 hover:bg-red-600 rounded text-white text-sm">Prøv igjen</button>
    </div>
  );
  if (!company) return <div className="text-slate-400">Bedrift ikke funnet</div>;

  const savedSteps = company.onboarding_steps || {};
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  // Auto-detected checks from real company data
  const agentCreated = !!company.agent_id;
  const phoneAssigned = !!company.phone_number;
  const clientInvited = !!(users && users.some(usr => usr.role === 'manager'));
  const testCallDone = !!savedSteps.test_call_done;
  const coreComplete = agentCreated && phoneAssigned && clientInvited && testCallDone;

  const ONBOARDING_ITEMS = [
    { id: 'agent_created',   label: 'AI-agent opprettet',         description: 'Retell AI-agent er konfigurert',            done: agentCreated,  isAuto: true },
    { id: 'phone_assigned',  label: 'Telefonnummer tilordnet',    description: 'Bedriften har fått et dedikert telefonnummer', done: phoneAssigned, isAuto: true },
    { id: 'client_invited',  label: 'Klient invitert',            description: 'En manager-bruker er opprettet for bedriften', done: clientInvited, isAuto: true },
    { id: 'test_call_done',  label: 'Test-samtale gjennomført',   description: 'Admin har verifisert at AI-en fungerer',       done: testCallDone,  isAuto: false },
    { id: 'onboarding_done', label: 'Onboarding fullført',        description: 'Alle øvrige steg er fullført',                 done: coreComplete,  isAuto: true },
  ];

  const onboardingSteps = ONBOARDING_ITEMS.map(item => ({
    key: item.id,
    label: item.label,
    description: item.description,
    done: item.done,
    isAuto: item.isAuto,
    date: item.done ? fmtDate(savedSteps[item.id] || company.created_at) : null,
  }));
  const onboardingCompletedCount = onboardingSteps.filter(s => s.done).length;
  const onboardingTotal = onboardingSteps.length;
  const onboardingPct = Math.round((onboardingCompletedCount / onboardingTotal) * 100);
  const allOnboardingDone = onboardingCompletedCount === onboardingTotal;
  // Auto-computed status (replaces manual Venter/Pågår/Fullført buttons)
  const computedStatus = onboardingCompletedCount >= 5 ? 'complete'
                       : onboardingCompletedCount >= 2 ? 'in_progress'
                       : 'pending';
  const computedStatusLabel = computedStatus === 'complete' ? 'Fullført'
                            : computedStatus === 'in_progress' ? 'Pågår'
                            : 'Venter';
  const computedStatusColor = computedStatus === 'complete' ? 'bg-accent-green/20 text-accent-green'
                            : computedStatus === 'in_progress' ? 'bg-accent-blue/20 text-accent-blue'
                            : 'bg-navy-700 text-slate-400';

  return (
    <div data-testid="admin-company-detail-page">
      <button onClick={() => navigate('/admin/companies')} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6" data-testid="back-to-companies">
        <ArrowLeft size={16} /> Tilbake til bedrifter
      </button>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-accent-blue/20 rounded-lg flex items-center justify-center">
            <Building2 size={20} className="text-accent-blue" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-white tracking-tight">{company.name}</h1>
            <p className="text-sm text-slate-500">Opprettet {company.created_at ? new Date(company.created_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing(!editing)} data-testid="edit-company-button"
            className="px-4 py-2 bg-navy-800 border border-navy-600 text-white text-sm rounded-lg hover:border-accent-blue transition-colors">
            {editing ? 'Avbryt' : 'Rediger'}
          </button>
          <button onClick={async () => { await api.put(`/api/admin/companies/${companyId}`, { active: !company.active }); loadData(); }}
            data-testid="toggle-active-button"
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${company.active ? 'bg-accent-red/15 text-accent-red hover:bg-accent-red/25' : 'bg-accent-green/15 text-accent-green hover:bg-accent-green/25'}`}>
            {company.active ? 'Deaktiver' : 'Aktiver'}
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="company-quick-actions">
        {/* TODO: Backend route POST /api/admin/companies/:id/send-invoice not yet implemented — hidden to avoid 404 toasts. */}
        {false && (
        <button
          onClick={async () => {
            try {
              await api.post(`/api/admin/companies/${companyId}/send-invoice`);
              toast.success('Faktura sendt til kunden');
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Kunne ikke sende faktura');
            }
          }}
          data-testid="qa-send-invoice"
          className="flex items-center gap-2 px-3 py-2 bg-navy-800 border border-navy-600 text-slate-300 hover:text-white hover:border-accent-blue text-xs rounded-lg transition-colors"
          title="Send siste faktura på e-post"
        >
          <Mail size={13} /> Send faktura
        </button>
        )}
        {/* TODO: Backend route POST /api/admin/companies/:id/agent/sync not yet implemented — hidden to avoid 404 toasts. */}
        {false && (
        <button
          onClick={async () => {
            if (!window.confirm('Synkroniser denne agenten med Retell AI? Dette laster ned siste agent-konfig.')) return;
            try {
              await api.post(`/api/admin/companies/${companyId}/agent/sync`);
              toast.success('Agent synkronisert med Retell');
              loadData();
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Kunne ikke synkronisere agent');
            }
          }}
          data-testid="qa-reset-agent"
          className="flex items-center gap-2 px-3 py-2 bg-navy-800 border border-navy-600 text-slate-300 hover:text-white hover:border-accent-blue text-xs rounded-lg transition-colors"
          title="Synkroniser agent-konfig fra Retell"
        >
          <RefreshCw size={13} /> Synk agent
        </button>
        )}
        <button
          onClick={async () => {
            try {
              const res = await api.get(`/api/admin/companies/${companyId}/export`, { responseType: 'blob' });
              const blob = new Blob([res.data], { type: 'application/json' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `${company.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-export-${new Date().toISOString().slice(0,10)}.json`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
              toast.success('Eksport lastet ned');
            } catch (err) {
              toast.error('Eksport ikke tilgjengelig ennå');
            }
          }}
          data-testid="qa-export"
          className="flex items-center gap-2 px-3 py-2 bg-navy-800 border border-navy-600 text-slate-300 hover:text-white hover:border-accent-blue text-xs rounded-lg transition-colors"
          title="Eksporter all data om denne kunden (JSON)"
        >
          <FileDown size={13} /> Eksport data
        </button>
        <button
          onClick={async () => {
            if (!suspendConfirm) { setSuspendConfirm(true); return; }
            setSuspendConfirm(false);
            try {
              await api.put(`/api/admin/companies/${companyId}`, { active: !company.active });
              toast.success(company.active ? 'Konto suspendert' : 'Konto reaktivert');
              loadData();
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Kunne ikke endre status');
            }
          }}
          data-testid="qa-suspend"
          className={`flex items-center gap-2 px-3 py-2 border text-xs rounded-lg transition-colors ${
            company.active
              ? 'border-accent-red/40 text-accent-red hover:bg-accent-red/10'
              : 'border-accent-green/40 text-accent-green hover:bg-accent-green/10'
          }`}
          title={company.active ? 'Stopp alle anrop og frys kontoen' : 'Reaktiver kontoen'}
        >
          <Ban size={13} /> {suspendConfirm ? 'Bekreft?' : (company.active ? 'Suspender' : 'Reaktiver')}
        </button>
        {suspendConfirm && (
          <button onClick={() => setSuspendConfirm(false)} className="text-xs px-2 py-1 border border-navy-600 text-slate-400 rounded-lg hover:bg-navy-700">Avbryt</button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => handleCompanyTabChange(id)}
            data-testid={`company-tab-${id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeTab === id ? 'bg-accent-blue text-white' : 'bg-navy-800 text-slate-400 border border-navy-600 hover:border-accent-blue'}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Edit form (still accessible globally for core fields) */}
      {editing && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[['name', 'Bedriftsnavn'], ['agent_id', 'Retell Agent ID'], ['phone_number', 'Telefonnummer'], ['contact_email', 'Kontakt-e-post'], ['contact_phone', 'Kontakttelefon']].map(([field, label]) => (
              <div key={field}>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">{label}</label>
                <input value={editForm[field] || ''} onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })}
                  className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue" />
              </div>
            ))}
            <div>
              <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Portalspråk</label>
              <select value={editForm.language || 'no'} onChange={(e) => setEditForm({ ...editForm, language: e.target.value })}
                className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
                <option value="no">🇳🇴 Norsk</option>
                <option value="en">🇺🇸 English</option>
              </select>
            </div>
          </div>
          <button onClick={handleUpdate} data-testid="save-company-button"
            className="px-5 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white font-medium rounded-lg transition-colors text-sm">Lagre</button>
        </div>
      )}

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'overview' && (
        <OverviewTab company={company} users={users} stats={stats} onboardingPct={onboardingPct} onboardingCompletedCount={onboardingCompletedCount} onboardingTotal={onboardingTotal} />
      )}

      {/* TAB 2: ONBOARDING */}
      {activeTab === 'onboarding' && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-heading text-base font-semibold text-white">Onboarding-sjekkliste</h3>
              <p className="text-xs text-slate-500 mt-1">Status oppdateres automatisk basert på faktisk data</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${computedStatusColor}`} data-testid="onboarding-status-badge">
                {allOnboardingDone && '✓ '}{computedStatusLabel}
              </span>
            </div>
          </div>

          {/* Progress bar with count */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-300" data-testid="onboarding-progress-label">{onboardingCompletedCount}/{onboardingTotal} steg fullført</span>
              <span className="text-xs text-slate-500">{onboardingPct}%</span>
            </div>
            <div className="w-full bg-navy-700 rounded-full h-2">
              <div className="bg-accent-green h-2 rounded-full transition-all" style={{ width: `${onboardingPct}%` }} />
            </div>
          </div>

          <div className="space-y-3">
            {onboardingSteps.map((step, i) => (
              <div key={step.key} className="flex items-center gap-4 p-3 rounded-lg hover:bg-navy-700/50 transition-colors group" data-testid={`onboarding-item-${step.key}`}>
                <button
                  disabled={step.isAuto}
                  onClick={async () => {
                    if (step.isAuto) return; // Auto items reflect reality; cannot toggle
                    const newSteps = { ...savedSteps, test_call_done: !savedSteps.test_call_done };
                    await api.put(`/api/admin/companies/${companyId}`, { onboarding_steps: newSteps });
                    loadData();
                  }}
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${step.done ? 'bg-accent-green/20' : 'bg-navy-700'} ${!step.isAuto ? 'hover:bg-navy-600 cursor-pointer' : 'cursor-default'}`}
                  title={step.isAuto ? 'Auto-detektert fra data' : (step.done ? 'Klikk for å angre' : 'Klikk for å markere som fullført')}
                >
                  {step.done ? <CheckCircle size={16} className="text-accent-green" /> : <span className="text-xs text-slate-500">{i + 1}</span>}
                </button>
                <div className="flex-1">
                  <span className={`text-sm ${step.done ? 'text-white' : 'text-slate-500'}`}>{step.label}</span>
                  {step.description && <span className="text-xs text-slate-600 ml-2">{step.description}</span>}
                  {step.date && <span className="text-xs text-slate-600 ml-2">— {step.date}</span>}
                </div>
                {step.isAuto ? (
                  <span className="text-xs text-slate-600">Auto</span>
                ) : (
                  !step.done && <span className="text-xs text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">Klikk for å fullføre</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 3: USERS (with credentials) */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {credentials && (
            <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <KeyRound size={16} className="text-accent-blue" />
                  <h3 className="font-heading text-base font-semibold text-white">Innloggingsinformasjon (primærbruker)</h3>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleResetPassword}
                    disabled={resettingPw}
                    data-testid="reset-password-button"
                    className={`flex items-center gap-2 px-3 py-2 border text-xs rounded-lg transition-colors disabled:opacity-50 ${
                      resetPwConfirm ? 'bg-accent-red/10 border-accent-red/40 text-accent-red' : 'bg-navy-700 border-navy-600 text-white hover:border-accent-blue'
                    }`}
                  >
                    <RefreshCw size={12} className={resettingPw ? 'animate-spin' : ''} />
                    {resetPwConfirm ? 'Bekreft tilbakestilling?' : 'Tilbakestill passord'}
                  </button>
                  {resetPwConfirm && (
                    <button onClick={() => setResetPwConfirm(false)} className="text-xs px-2 py-1 border border-navy-600 text-slate-400 rounded-lg hover:bg-navy-700">Avbryt</button>
                  )}
                  {/* "Logg inn som klient" was removed in sprint 3 — it only opened /login in
                      a new tab, which is misleading. Real impersonation requires a signed
                      admin-only JWT and an audit log; pick that up in a later sprint. */}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">E-post</div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-white break-all">{credentials.username || credentials.email}</span>
                    <button onClick={() => copyToClipboard(credentials.username || credentials.email)} className="text-slate-500 hover:text-white" title="Kopier">
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Passord</div>
                  {credentials.temp_password_available ? (
                    <div className="space-y-1">
                      <span className="text-sm text-slate-300">Midlertidig passord tilgjengelig — klikk Tilbakestill for å se det</span>
                      {credentials.temp_password_expires_at && (
                        <div className="text-xs text-slate-500">Utløper {new Date(credentials.temp_password_expires_at).toLocaleString('nb-NO')}</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-slate-500 italic">Ingen midlertidige passord er tilgjengelige</span>
                  )}
                </div>
              </div>
              {newPwReveal && (
                <div className="mt-4 p-4 bg-accent-green/10 border border-accent-green/30 rounded-lg">
                  <div className="text-xs font-semibold tracking-widest uppercase text-accent-green mb-2">Nytt passord generert</div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-mono text-white">{newPwReveal}</span>
                    <button onClick={() => copyToClipboard(newPwReveal)} className="flex items-center gap-1 px-2 py-1 bg-navy-800 border border-navy-600 text-white text-xs rounded hover:border-accent-blue">
                      <Copy size={12} /> Kopier
                    </button>
                    <button onClick={() => setNewPwReveal(null)} className="ml-auto text-slate-500 hover:text-white">
                      <X size={14} />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Kopier dette nå — det vises kun denne éne gangen og fjernes automatisk etter 60 sekunder.</p>
                </div>
              )}
            </div>
          )}

          <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy-600">
              <h3 className="font-heading text-base font-semibold text-white">Brukere ({users.length})</h3>
              <button onClick={() => setShowInvite(true)} data-testid="invite-user-button"
                className="flex items-center gap-2 px-3 py-2 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm rounded-lg transition-colors">
                <UserPlus size={14} /> Inviter bruker
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="company-users-table">
                <thead><tr className="border-b border-navy-600">
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Navn</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">E-post</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Rolle</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Sist pålogget</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Handlinger</th>
                </tr></thead>
                <tbody>
                  {users.map(u => {
                    const invited = u.invited_at && !u.last_login && !u.accepted_at;
                    return (
                      <tr key={u._id} className="border-b border-navy-600/50">
                        <td className="px-4 py-3.5 text-sm text-white">{u.name || '—'}</td>
                        <td className="px-4 py-3.5 text-sm text-slate-400">{u.email}</td>
                        <td className="px-4 py-3.5"><span className="text-xs px-2 py-1 bg-navy-700 rounded text-slate-300">{u.role === 'admin' ? 'Admin' : u.role === 'manager' ? 'Manager' : 'Klient'}</span></td>
                        <td className="px-4 py-3.5 text-xs text-slate-400">{formatRelativeDaysNO(u.last_login)}</td>
                        <td className="px-4 py-3.5">
                          <span className={`text-xs px-2 py-1 rounded ${invited ? 'bg-accent-yellow/15 text-accent-yellow' : (u.active !== false ? 'bg-accent-green/15 text-accent-green' : 'bg-accent-red/15 text-accent-red')}`}>
                            {invited ? 'Invitert' : (u.active !== false ? 'Aktiv' : 'Deaktivert')}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          {removeUserConfirm === u._id ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-accent-red">Fjern?</span>
                              <button onClick={() => handleRemoveUser(u._id)} className="text-xs px-1.5 py-0.5 bg-accent-red/20 text-accent-red rounded">Ja</button>
                              <button onClick={() => setRemoveUserConfirm(null)} className="text-xs px-1.5 py-0.5 border border-navy-600 text-slate-400 rounded">Nei</button>
                            </div>
                          ) : (
                            <button onClick={() => handleRemoveUser(u._id)} className="text-slate-500 hover:text-accent-red transition-colors" title="Fjern bruker"><Trash2 size={14} /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {users.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">Ingen brukere ennå</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: AGENT CONFIG */}
      {activeTab === 'agent' && (
        <CompanyAgentConfig companyId={companyId} toast={toast} />
      )}

      {/* TAB 5: BILLING */}
      {activeTab === 'billing' && (
        <CompanyBilling
          companyId={companyId}
          company={company}
          editForm={editForm}
          setEditForm={setEditForm}
          reload={loadData}
          toast={toast}
        />
      )}

      {/* TAB 6: CALLS */}
      {activeTab === 'calls' && (
        <CompanyCalls calls={recentCalls} companyName={company.name} navigate={navigate} />
      )}

      {/* TAB 7: CONTACTS */}
      {activeTab === 'contacts' && (
        <CompanyContacts companyId={companyId} toast={toast} />
      )}

      {/* TAB 8: SETTINGS */}
      {activeTab === 'settings' && (
        <CompanySettings
          companyId={companyId}
          editForm={editForm}
          setEditForm={setEditForm}
          reload={loadData}
          toast={toast}
        />
      )}

      {/* TAB 9: INTEGRATIONS */}
      {activeTab === 'integrations' && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <div className="text-center py-12 text-navy-400">
            <Plug className="mx-auto mb-4 opacity-40" size={48} />
            <p className="text-slate-400">Integrasjoner kommer snart — Google Calendar, Fiken og mer.</p>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" data-testid="invite-modal">
          <div className="bg-navy-800 border border-navy-600 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold text-white">Inviter bruker</h2>
              <button onClick={() => { setShowInvite(false); setInviteLink(''); }} className="text-slate-500 hover:text-white"><X size={18} /></button>
            </div>
            {!inviteLink ? (
              <div className="space-y-4">
                <input data-testid="invite-email-input" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue" placeholder="bruker@bedrift.no" />
                <button onClick={handleInvite} data-testid="send-invite-button"
                  className="w-full py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors">Generer invitasjonslenke</button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-400 mb-3">Del denne lenken med brukeren:</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={inviteLink} data-testid="invite-link-display"
                    className="flex-1 px-3 py-2.5 bg-navy-900 border border-navy-600 rounded-lg text-xs text-slate-300 font-mono truncate" />
                  <button onClick={copyLink} data-testid="copy-invite-link"
                    className="p-2.5 bg-accent-blue rounded-lg text-white hover:bg-accent-blue-hover transition-colors">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* TAB COMPONENTS                                                            */
/* ------------------------------------------------------------------------- */

function OverviewTab({ company, users, stats, onboardingPct, onboardingCompletedCount, onboardingTotal }) {
  return (
    <div className="space-y-6">
      {/* Top row: key metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Samtaler denne måneden</div>
          <div className="text-2xl font-heading font-bold text-white">{stats?.calls_this_month || 0}</div>
          {stats?.trend_pct !== undefined && stats.trend_pct !== 0 && (
            <div className={`text-xs mt-1 ${stats.trend_pct > 0 ? 'text-accent-green' : 'text-accent-red'}`}>
              {stats.calls_prev_month < 5 ? `+${stats.calls_this_month - stats.calls_prev_month} flere` : `${stats.trend_pct > 0 ? '+' : ''}${stats.trend_pct}%`} vs. forrige måned
            </div>
          )}
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Kostnad denne måneden</div>
          <div className="text-2xl font-heading font-bold text-accent-red">{(stats?.total_cost || 0).toLocaleString('nb-NO')} kr</div>
          <div className="text-xs text-slate-500 mt-1">Snitt {stats?.avg_cost_per_call || 0} kr/samtale</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Månedlig inntekt (MRR)</div>
          <div className="text-2xl font-heading font-bold text-accent-green">{(stats?.monthly_revenue || 0).toLocaleString('nb-NO')} kr</div>
          {stats?.monthly_revenue > 0 && stats?.total_cost > 0 && (
            <div className="text-xs text-accent-green mt-1">Margin: {Math.round(((stats.monthly_revenue - stats.total_cost) / stats.monthly_revenue) * 100)}%</div>
          )}
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Siste samtale</div>
          <div className="text-sm font-medium text-white">{stats?.last_call_at ? (() => {
            const d = new Date(stats.last_call_at); const diff = Math.max(0, Math.floor((Date.now() - d) / 1000));
            if (diff < 60) return 'Akkurat nå';
            if (diff < 3600) return `${Math.floor(diff/60)} min siden`;
            if (diff < 86400) return `${Math.floor(diff/3600)} timer siden`;
            return `${Math.floor(diff/86400)} dager siden`;
          })() : '—'}</div>
          {stats?.last_webhook_at && (
            <div className={`text-xs mt-1 ${stats.last_webhook_success ? 'text-accent-green' : 'text-accent-red'}`}>
              Webhook: {stats.last_webhook_success ? 'OK' : 'Feilet'}
            </div>
          )}
        </div>
        {/* Onboarding progress card */}
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Onboarding</div>
          <div className="text-2xl font-heading font-bold text-white">{onboardingCompletedCount ?? '?'}/{onboardingTotal ?? '?'}</div>
          <div className="text-xs text-slate-500 mt-1">steg fullført</div>
          <div className="w-full bg-navy-700 rounded-full h-1.5 mt-2">
            <div className="bg-accent-green h-1.5 rounded-full transition-all" style={{ width: `${onboardingPct}%` }} />
          </div>
        </div>
      </div>

      {/* Company info row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Telefonnummer</div>
          <div className="text-sm font-mono text-white">{company.phone_number || '—'}</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Retell Agent ID</div>
          <div className="text-xs font-mono text-slate-300 break-all">{company.agent_id || '—'}</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Status</div>
          <span className={`text-sm font-medium ${company.active ? 'text-accent-green' : 'text-accent-red'}`}>{company.active ? 'Aktiv' : 'Inaktiv'}</span>
          <span className="text-xs text-slate-500 ml-2">/ {users.length} brukere</span>
        </div>
      </div>
    </div>
  );
}

/* --- Tab 4: Agent config for a company ------------------------------------ */
function CompanyAgentConfig({ companyId, toast }) {
  const [activeSubTab, setActiveSubTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [backendMissing, setBackendMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    greeting: '',
    transfer_number: '',
    business_name: '',
    voice: 'kari',
  });

  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [testMessage, setTestMessage] = useState('');

  const [playingVoice, setPlayingVoice] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState('kari');
  const [savingVoice, setSavingVoice] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get(`/api/admin/companies/${companyId}/agent-config`);
        setForm({
          greeting: data.greeting || '',
          transfer_number: data.transfer_number || '',
          business_name: data.business_name || '',
          voice: data.voice || 'kari',
        });
        setSelectedVoice(data.voice || 'kari');
      } catch (err) {
        if (err.response?.status === 404) {
          setBackendMissing(true);
        } else {
          toast.error('Kunne ikke laste agent-konfigurasjon');
        }
      } finally {
        setLoading(false);
      }
    }
    load();
    // eslint-disable-next-line
  }, [companyId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/admin/companies/${companyId}/agent-config`, form);
      toast.success('Konfigurasjon lagret');
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Backend ikke klar ennå' : 'Kunne ikke lagre');
    } finally { setSaving(false); }
  };

  const handleTestCall = async () => {
    if (!testPhone) { toast.error('Skriv inn et telefonnummer'); return; }
    setTesting(true); setTestMessage('');
    try {
      await api.post(`/api/admin/companies/${companyId}/agent-config/test-call`, { phone_number: testPhone });
      setTestMessage('AI-resepsjonisten ringer om noen sekunder...');
      toast.success('Testanrop startet');
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Testanrop ikke tilgjengelig ennå' : 'Kunne ikke starte testanrop');
    } finally { setTesting(false); }
  };

  const handlePlayVoice = async (voiceId) => {
    if (playingVoice === voiceId) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingVoice(null);
      return;
    }
    try {
      const response = await api.get(`/api/admin/companies/${companyId}/agent-config/voice-preview?voice=${voiceId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }));
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = url;
        audioRef.current.onended = () => {
          setPlayingVoice(null);
          URL.revokeObjectURL(url);
        };
        await audioRef.current.play();
        setPlayingVoice(voiceId);
      } else {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Forhåndsvisning ikke tilgjengelig' : 'Kunne ikke spille av stemme');
    }
  };

  const handleSaveVoice = async () => {
    setSavingVoice(true);
    try {
      await api.patch(`/api/admin/companies/${companyId}/agent-config`, { voice: selectedVoice });
      setForm(f => ({ ...f, voice: selectedVoice }));
      toast.success('Stemme lagret');
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Backend ikke klar ennå' : 'Kunne ikke lagre stemme');
    } finally { setSavingVoice(false); }
  };

  const subTabs = [
    { id: 'config', label: 'Konfigurasjon', icon: SettingsIcon },
    { id: 'test',   label: 'Test agent',    icon: PhoneCall },
    { id: 'voice',  label: 'Stemme',        icon: Mic },
  ];

  if (loading) return <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 animate-pulse h-64" />;

  if (backendMissing) {
    return (
      <div className="bg-navy-800 border border-navy-600 rounded-lg p-10 text-center">
        <Mic size={32} className="mx-auto text-slate-600 mb-3" />
        <h3 className="font-heading text-lg font-semibold text-white mb-2">Agent-konfigurasjon kommer</h3>
        <p className="text-sm text-slate-400">Backend under utvikling. Endepunktet <code className="text-xs bg-navy-900 px-1.5 py-0.5 rounded">/api/admin/companies/:id/agent-config</code> er ikke implementert ennå.</p>
      </div>
    );
  }

  return (
    <div data-testid="company-agent-config">
      <audio ref={audioRef} hidden />

      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {subTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveSubTab(id)}
            data-testid={`agent-subtab-${id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeSubTab === id ? 'bg-accent-blue text-white' : 'bg-navy-800 text-slate-400 border border-navy-600 hover:border-accent-blue'
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {activeSubTab === 'config' && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 space-y-5">
          <h3 className="font-heading text-base font-semibold text-white">Grunnleggende innstillinger</h3>

          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Hilsningstekst</label>
            <textarea
              rows={4}
              value={form.greeting}
              onChange={(e) => setForm({ ...form, greeting: e.target.value })}
              placeholder="Hei, du har kommet til [bedrift]. Hvordan kan jeg hjelpe deg?"
              className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue resize-none"
            />
            <p className="text-xs text-slate-500 mt-1.5">Dette er det første innringere hører når de ringer.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Videresendingsnummer</label>
            <input
              type="text"
              value={form.transfer_number}
              onChange={(e) => setForm({ ...form, transfer_number: e.target.value })}
              placeholder="+47 XXX XX XXX"
              className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Bedriftsnavn (som AI presenterer seg med)</label>
            <input
              type="text"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              placeholder="Din Bedrift AS"
              className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
            />
          </div>

          <div className="pt-3 border-t border-navy-600">
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="agent-config-save"
              className="flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? 'Lagrer...' : 'Lagre endringer'}
            </button>
          </div>
        </div>
      )}

      {activeSubTab === 'test' && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 space-y-5">
          <h3 className="font-heading text-base font-semibold text-white">Test AI-resepsjonisten</h3>
          <p className="text-sm text-slate-400">Ring et valgfritt nummer for å teste agenten for denne bedriften.</p>

          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Ring dette nummeret</label>
            <input
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+47 XXX XX XXX"
              className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue font-mono"
            />
          </div>

          <button
            onClick={handleTestCall}
            disabled={testing || !testPhone}
            data-testid="agent-test-call-button"
            className="flex items-center gap-2 px-6 py-3 bg-accent-green hover:bg-accent-green/80 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            {testing ? <Loader size={16} className="animate-spin" /> : <PhoneCall size={16} />}
            Start testanrop
          </button>

          {testMessage && (
            <div className="mt-4 p-4 bg-accent-green/10 border border-accent-green/30 rounded-lg flex items-center gap-3">
              <CheckCircle size={18} className="text-accent-green flex-shrink-0" />
              <span className="text-sm text-accent-green">{testMessage}</span>
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'voice' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {VOICES.map(v => {
              const selected = selectedVoice === v.id;
              const playing = playingVoice === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => setSelectedVoice(v.id)}
                  data-testid={`voice-option-${v.id}`}
                  className={`text-left bg-navy-800 border rounded-lg p-6 transition-all ${
                    selected ? 'border-accent-green ring-2 ring-accent-green/40' : 'border-navy-600 hover:border-accent-blue'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white">{v.name} <span className="text-sm font-normal text-slate-500">({v.gender})</span></h3>
                      <p className="text-sm text-slate-400 mt-1">{v.description}</p>
                    </div>
                    {selected && <CheckCircle size={18} className="text-accent-green flex-shrink-0" />}
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); handlePlayVoice(v.id); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handlePlayVoice(v.id); } }}
                    className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-navy-700 border border-navy-600 rounded-lg text-sm text-slate-300 hover:border-accent-blue hover:text-white transition-colors cursor-pointer"
                  >
                    {playing ? <Pause size={14} /> : <Play size={14} />}
                    {playing ? 'Stopp' : 'Spill av'}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="pt-3">
            <button
              onClick={handleSaveVoice}
              disabled={savingVoice}
              data-testid="voice-save-button"
              className="flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {savingVoice ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
              Lagre stemmevalg
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Tab 5: Billing for this company ------------------------------------- */
function CompanyBilling({ companyId, company, editForm, setEditForm, reload, toast }) {
  const [usage, setUsage] = useState(null);
  const [usageMissing, setUsageMissing] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesMissing, setInvoicesMissing] = useState(false);
  const [subLoading, setSubLoading] = useState(true);

  const [planDraft, setPlanDraft] = useState(company.subscription_tier || 'Start');
  const [savingPlan, setSavingPlan] = useState(false);
  const [planEndpointMissing, setPlanEndpointMissing] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get(`/api/admin/companies/${companyId}/usage`);
        if (mounted) setUsage(data);
      } catch (err) {
        if (err.response?.status === 404 && mounted) setUsageMissing(true);
      }
      try {
        const { data } = await api.get(`/api/admin/companies/${companyId}/invoices`);
        if (mounted) setInvoices(data.invoices || data || []);
      } catch (err) {
        if (err.response?.status === 404 && mounted) setInvoicesMissing(true);
      }
      if (mounted) setSubLoading(false);
    }
    load();
    return () => { mounted = false; };
  }, [companyId]);

  const savePlan = async () => {
    setSavingPlan(true);
    try {
      await api.patch(`/api/admin/companies/${companyId}/subscription`, { subscription_tier: planDraft });
      toast.success('Abonnement oppdatert');
      reload();
    } catch (err) {
      if (err.response?.status === 404) {
        setPlanEndpointMissing(true);
        toast.info('Abonnement-endepunktet er ikke klart ennå');
      } else {
        toast.error('Kunne ikke oppdatere abonnement');
      }
    } finally { setSavingPlan(false); }
  };

  const saveMRR = async () => {
    try {
      await api.put(`/api/admin/companies/${companyId}`, { monthly_revenue: editForm.monthly_revenue });
      toast.success('MRR oppdatert');
      reload();
    } catch (err) { toast.error('Kunne ikke lagre'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const formatAmount = (a, c) => a == null ? '—' : `${Number(a).toLocaleString('nb-NO')} ${(c || 'NOK').toUpperCase()}`;

  const currentPlan = company.subscription_tier || 'Start';
  const currentPrice = PLANS[currentPlan] || 0;

  return (
    <div className="space-y-6">
      {/* Current plan card */}
      <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Nåværende abonnement</div>
            <h2 className="font-heading text-xl font-bold text-white">{currentPlan}</h2>
            <p className="text-sm text-slate-400 mt-1">{currentPrice.toLocaleString('nb-NO')} NOK/mnd</p>
          </div>
          <span className={`text-xs px-2 py-1 rounded ${company.active ? 'bg-accent-green/15 text-accent-green' : 'bg-slate-500/20 text-slate-400'}`}>
            {company.active ? 'Aktiv' : 'Inaktiv'}
          </span>
        </div>

        <div className="pt-5 border-t border-navy-600">
          <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Endre plan</label>
          <div className="flex gap-2">
            <select value={planDraft} onChange={(e) => setPlanDraft(e.target.value)}
              className="flex-1 px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
              {Object.entries(PLANS).map(([name, price]) => (
                <option key={name} value={name}>{name} — {price.toLocaleString('nb-NO')} NOK/mnd</option>
              ))}
            </select>
            <button onClick={savePlan} disabled={savingPlan || planEndpointMissing}
              className="px-4 py-2 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm rounded-lg transition-colors disabled:opacity-50">
              {savingPlan ? <Loader size={14} className="animate-spin" /> : 'Lagre'}
            </button>
          </div>
          {planEndpointMissing && (
            <p className="text-xs text-slate-500 mt-2">Abonnement-endepunktet er ikke klart ennå. Bruk MRR-editor under i mellomtiden.</p>
          )}
        </div>
      </div>

      {/* Usage meter */}
      {usageMissing ? (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 text-center text-sm text-slate-500">
          Forbruks-endepunktet er ikke implementert ennå (<code className="text-xs bg-navy-900 px-1.5 py-0.5 rounded">/api/admin/companies/:id/usage</code>).
        </div>
      ) : usage ? (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Minutt-forbruk denne måneden</div>
              <h2 className="font-heading text-xl font-bold text-white">
                {usage.used_minutes || 0} <span className="text-slate-500 font-normal text-base">av {usage.plan_limit || 0} minutter</span>
              </h2>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Plan</div>
              <div className="text-lg font-heading font-bold text-white">{usage.plan_name || currentPlan}</div>
            </div>
          </div>
          <div className="w-full h-4 bg-navy-900 border border-navy-600 rounded-full overflow-hidden">
            <div className={`h-full transition-all duration-500 ${usage.status === 'critical' ? 'bg-accent-red' : usage.status === 'warning' ? 'bg-accent-yellow' : 'bg-accent-green'}`}
              style={{ width: `${Math.max(0, Math.min(100, usage.pct_used || 0))}%` }} />
          </div>
          <div className="text-xs text-slate-500 mt-2">{usage.pct_used || 0}% brukt</div>
        </div>
      ) : (
        !subLoading && null
      )}

      {/* Manual MRR override */}
      <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
        <label className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2 block">Månedlig inntekt (manuell overstyring) (kr)</label>
        <div className="flex gap-2">
          <input type="number" value={editForm.monthly_revenue || ''} onChange={(e) => setEditForm({...editForm, monthly_revenue: parseFloat(e.target.value) || 0})}
            className="flex-1 px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue" placeholder="0" />
          <button onClick={saveMRR}
            className="px-4 py-2 bg-accent-blue text-white text-sm rounded-lg hover:bg-accent-blue-hover transition-colors">Lagre</button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Bruk dette for å overstyre beregnet MRR fra abonnementet (f.eks. for rabatter eller manuelle avtaler).</p>
      </div>

      {/* Invoices */}
      <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-navy-600">
          <h3 className="font-heading text-base font-semibold text-white">Fakturahistorikk</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-600">
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Dato</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Beløp</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoicesMissing || invoices.length === 0 ? (
                <tr><td colSpan="4" className="px-4 py-12 text-center text-slate-500">Ingen fakturaer ennå</td></tr>
              ) : invoices.map(inv => (
                <tr key={inv.id || inv._id} className="border-b border-navy-600/50 hover:bg-navy-700/50 transition-colors">
                  <td className="px-4 py-3.5 text-sm text-slate-300">{formatDate(inv.date || inv.created_at || inv.issued_at)}</td>
                  <td className="px-4 py-3.5 text-sm text-white font-mono">{formatAmount(inv.amount ?? inv.total ?? inv.amount_nok, inv.currency)}</td>
                  <td className="px-4 py-3.5"><span className="text-xs px-2 py-1 rounded bg-slate-500/20 text-slate-400">{inv.status || '—'}</span></td>
                  <td className="px-4 py-3.5 text-right">
                    {inv.pdf_url || inv.invoice_pdf ? (
                      <a href={inv.pdf_url || inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-accent-blue hover:text-accent-blue-hover transition-colors">
                        <Download size={14} /> Last ned
                      </a>
                    ) : <span className="text-xs text-slate-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* --- Tab 6: Calls (enhanced) --------------------------------------------- */
function CompanyCalls({ calls, companyName, navigate }) {
  const [search, setSearch] = useState('');
  const [range, setRange] = useState('all');

  const now = Date.now();
  const filtered = calls.filter(c => {
    const _ca = c.call_analysis || {};
    const a = { ..._ca, ...(_ca.custom_analysis_data || {}), caller_name: c.caller_name ?? _ca.caller_name };
    // Search by caller phone or name
    if (search) {
      const s = search.toLowerCase();
      const hay = `${c.from_number || ''} ${c.to_number || ''} ${a.caller_name || ''}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    if (range !== 'all') {
      const t = new Date(c.created_at).getTime();
      const days = range === 'week' ? 7 : 30;
      if (now - t > days * 86400000) return false;
    }
    return true;
  });

  const exportCsv = () => {
    const rows = [['Tidspunkt', 'Innringer', 'Telefon', 'Henvendelse', 'Lead score']];
    filtered.forEach(call => {
      const _ca = call.call_analysis || {};
      const a = { ..._ca, ...(_ca.custom_analysis_data || {}) };
      rows.push([
        new Date(call.created_at).toISOString(),
        a.caller_name || call.caller_name || '',
        call.from_number || '',
        a.call_intent || call.call_intent || '',
        call.lead_score ?? a.lead_score ?? '',
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${companyName || 'samtaler'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-navy-600 flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <h3 className="font-heading text-base font-semibold text-white">Samtaler ({filtered.length})</h3>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Søk nummer/navn..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue w-48" />
          </div>
          <select value={range} onChange={(e) => setRange(e.target.value)}
            className="px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
            <option value="week">Denne uken</option>
            <option value="month">Denne måneden</option>
            <option value="all">All tid</option>
          </select>
          <button onClick={exportCsv}
            className="flex items-center gap-2 px-3 py-2 bg-navy-700 border border-navy-600 text-white text-sm rounded-lg hover:border-accent-blue transition-colors">
            <Download size={14} /> Eksporter CSV
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-navy-600">
            <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Innringer</th>
            <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Telefon</th>
            <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Henvendelse</th>
            <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Lead</th>
            <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Tidspunkt</th>
          </tr></thead>
          <tbody>
            {filtered.map(call => {
              const _ca = call.call_analysis || {};
              const a = {
                ..._ca, ...(_ca.custom_analysis_data || {}),
                lead_score: call.lead_score ?? _ca.lead_score,
                call_intent: call.call_intent ?? _ca.call_intent,
                caller_name: call.caller_name ?? _ca.caller_name,
              };
              return (
                <tr key={call._id} onClick={() => navigate(`/calls/${call.call_id}`)}
                  className="border-b border-navy-600/50 hover:bg-navy-700 cursor-pointer transition-colors">
                  <td className="px-4 py-3.5 text-sm text-white">{a.caller_name || 'Ukjent'}</td>
                  <td className="px-4 py-3.5 text-xs font-mono text-slate-400">{call.from_number || '—'}</td>
                  <td className="px-4 py-3.5"><span className="text-xs px-2 py-1 bg-navy-700 rounded text-slate-300">{a.call_intent || '—'}</span></td>
                  <td className="px-4 py-3.5"><span className={`text-sm font-mono font-medium ${(a.lead_score || 0) >= 80 ? 'text-accent-green' : 'text-slate-400'}`}>{a.lead_score || 0}</span></td>
                  <td className="px-4 py-3.5 text-sm text-slate-400">{new Date(call.created_at).toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">Ingen samtaler matcher filteret</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --- Tab 7: Contacts ------------------------------------------------------ */
function CompanyContacts({ companyId, toast }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get(`/api/contacts?company_id=${companyId}`);
        if (mounted) setContacts(data.contacts || data || []);
      } catch (err) {
        if (mounted) setContacts([]);
      } finally { if (mounted) setLoading(false); }
    }
    load();
    return () => { mounted = false; };
  }, [companyId]);

  const filtered = contacts.filter(c =>
    !search ||
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.phone_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('company_id', companyId);
    try {
      await api.post('/api/contacts/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Kontakter importert');
      const { data } = await api.get(`/api/contacts?company_id=${companyId}`);
      setContacts(data.contacts || data || []);
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Import ikke tilgjengelig ennå' : 'Kunne ikke importere kontakter');
    }
    e.target.value = '';
  };

  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-navy-600 flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <h3 className="font-heading text-base font-semibold text-white">Kontakter ({filtered.length})</h3>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Søk..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue w-48" />
          </div>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImport} className="hidden" />
          <button onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm rounded-lg transition-colors">
            <Upload size={14} /> Importer kontakter
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Laster...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Users size={32} className="mx-auto mb-3 opacity-40" />
            <p>Ingen kontakter ennå</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-600">
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Navn</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Telefon</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">E-post</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Tags</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Siste kontakt</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c._id || c.id} className="border-b border-navy-600/50 hover:bg-navy-700/50 transition-colors">
                  <td className="px-4 py-3.5 text-sm text-white">{c.name || '—'}</td>
                  <td className="px-4 py-3.5 text-xs font-mono text-slate-400">{c.phone_number || '—'}</td>
                  <td className="px-4 py-3.5 text-sm text-slate-400">{c.email || '—'}</td>
                  <td className="px-4 py-3.5">
                    {Array.isArray(c.tags) && c.tags.length > 0 ? (
                      <div className="flex gap-1 flex-wrap">
                        {c.tags.map((t, i) => <span key={i} className="text-xs px-2 py-0.5 bg-navy-700 rounded text-slate-300">{t}</span>)}
                      </div>
                    ) : <span className="text-xs text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3.5 text-xs text-slate-400">{formatRelativeDaysNO(c.last_interaction || c.last_interaction_at || c.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* --- Tab 8: Settings ------------------------------------------------------ */
function CompanySettings({ companyId, editForm, setEditForm, reload, toast }) {
  const [savingAll, setSavingAll] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);

  const saveAll = async () => {
    setSavingAll(true);
    try {
      await api.put(`/api/admin/companies/${companyId}`, {
        business_hours: editForm.business_hours,
        transfer_number: editForm.transfer_number,
        brand: editForm.brand,
        admin_notes: editForm.admin_notes,
        webhook_url: editForm.webhook_url,
      });
      toast.success('Innstillinger lagret');
      reload();
    } catch (err) { toast.error('Kunne ikke lagre alle felter'); }
    finally { setSavingAll(false); }
  };

  const testWebhook = async () => {
    if (!editForm.webhook_url) { toast.error('Legg inn webhook URL først'); return; }
    setTestingWebhook(true);
    try {
      await api.post(`/api/admin/companies/${companyId}/test-webhook`, { webhook_url: editForm.webhook_url });
      toast.success('Webhook testet — sjekk loggene');
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Webhook-test ikke tilgjengelig' : 'Webhook-test feilet');
    } finally { setTestingWebhook(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
        <label className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2 block">Åpningstider</label>
        <textarea value={editForm.business_hours || ''} onChange={(e) => setEditForm({...editForm, business_hours: e.target.value})} rows={3}
          className="w-full px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue resize-none"
          placeholder="Man–Fre 08:00–16:00" />
      </div>

      <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
        <label className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2 block">Videresendingsnummer (Transfer)</label>
        <div className="flex gap-2">
          <input type="text" value={editForm.transfer_number || ''} onChange={(e) => setEditForm({...editForm, transfer_number: e.target.value})}
            className="flex-1 px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue" placeholder="+47 XXX XX XXX" />
          <button onClick={async () => { await api.post(`/api/admin/companies/${companyId}/update-transfer`, { transfer_number: editForm.transfer_number || '' }); toast.success('Videresendingsnummer lagret'); reload(); }}
            className="px-4 py-2 bg-accent-blue text-white text-sm rounded-lg hover:bg-accent-blue-hover transition-colors">Oppdater</button>
        </div>
        <p className="text-xs text-slate-500 mt-1.5">Nummeret som brukes når innringer ber om å snakke med et menneske.</p>
      </div>

      <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
        <label className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2 block">Brand / Portal</label>
        <div className="flex gap-2">
          <select value={editForm.brand || 'no'} onChange={(e) => setEditForm({...editForm, brand: e.target.value})}
            className="flex-1 px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
            <option value="no">AI Resepsjonen (portal.airesepsjonen.no)</option>
            <option value="en">AI Reception (portal.ai-reception.io)</option>
          </select>
          <button onClick={async () => { await api.patch(`/api/companies/settings`, { company_id: companyId, brand: editForm.brand }); toast.success('Brand lagret'); reload(); }}
            className="px-4 py-2 bg-accent-blue text-white text-sm rounded-lg hover:bg-accent-blue-hover transition-colors">Lagre</button>
        </div>
        <p className="text-xs text-slate-500 mt-1.5">Hvilken portal/brand denne bedriften hører til.</p>
      </div>

      <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
        <label className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2 block">
          <Globe size={12} className="inline mr-1" /> Webhook URL
        </label>
        <div className="flex gap-2">
          <input type="url" value={editForm.webhook_url || ''} onChange={(e) => setEditForm({...editForm, webhook_url: e.target.value})}
            className="flex-1 px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-2 focus:ring-accent-blue" placeholder="https://example.com/webhook" />
          <button onClick={testWebhook} disabled={testingWebhook || !editForm.webhook_url}
            className="px-4 py-2 bg-navy-700 border border-navy-600 text-white text-sm rounded-lg hover:border-accent-blue transition-colors disabled:opacity-50">
            {testingWebhook ? <Loader size={14} className="animate-spin" /> : 'Test'}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1.5">Kundens eget endepunkt hvor vi sender samtale-events.</p>
      </div>

      <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
        <label className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2 block">Interne notater</label>
        <textarea value={editForm.admin_notes || ''} onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})} rows={4}
          className="w-full px-3 py-2 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue resize-none" placeholder="Notater om denne klienten..." />
      </div>

      <div>
        <button onClick={saveAll} disabled={savingAll}
          className="flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors disabled:opacity-50">
          {savingAll ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
          Lagre alle innstillinger
        </button>
      </div>
    </div>
  );
}
